import { ReactElement } from 'react'

import { CartGridRowProps } from '../../client/components/cart/cart.js'
import { ImageProps } from '../../client/components/image/image.js'
import { TagProps } from '../../client/components/tag/tag.js'
import { Customer } from '../../client/models/customer.js'
import { ClaimStatus, ClaimType } from '../enums.js'
import { ErrorInSubscriptionDataSimple } from '../errors/subscription-errors.js'
import { isValidUrl } from '../helpers.js'
import { translate } from '../i18n.js'
import { SubscriptionClaimDTO, SubscriptionProductDTO } from '../types.js'
import { getPlanDurationInMonths } from '../utils/utils.js'
import { SubscriptionProductPlan } from './subscription-product-plan.js'
import { SubscriptionProductService } from './subscription-product-service.js'

export interface SubscriptionProductDiscounts {
    general: number
    ignoreVat: number
}

export interface ProductCartRows {
    monthly: number
    rows: CartGridRowProps[]
}

export abstract class CommonSubscriptionProduct {
    readonly data: SubscriptionProductDTO
    readonly plan: SubscriptionProductPlan
    readonly possiblePlans: SubscriptionProductPlan[]
    readonly services: SubscriptionProductService[]
    readonly currencySymbol: string
    readonly locale: string

    protected debugFlag = false

    public overrideTotalMinimalCost?: number
    public customer?: Customer

    constructor(product: SubscriptionProductDTO, currencySymbol: string, locale: string) {
        this.data = product
        this.currencySymbol = currencySymbol
        this.locale = locale

        this.possiblePlans =
            this.data.possiblePlans?.map((planPrice) => {
                return new SubscriptionProductPlan(
                    planPrice,
                    this.locale,
                    getPlanDurationInMonths(
                        planPrice.plan?.durationInMonths || 0,
                        planPrice.plan?.code,
                        planPrice?.country?.code,
                    ),
                )
            }) || []
        this.services =
            this.data.services?.map((service) => new SubscriptionProductService(service, this.currencySymbol)) || []
        if (!this.data.planPrice) {
            throw new ErrorInSubscriptionDataSimple('C1')
        }

        // For presigned subscriptions, the product's planPriceWithVat/planPriceWithoutVat
        // are overridden to the presigned monthly price, while planPrice (the catalog relation)
        // still has the standard catalog price. Use the product's stored values if available.
        const planPriceData = {
            ...this.data.planPrice,
            priceWithVat: this.data.planPriceWithVat || this.data.planPrice.priceWithVat,
            priceWithoutVat: this.data.planPriceWithoutVat || this.data.planPrice.priceWithoutVat,
        }

        this.plan = new SubscriptionProductPlan(
            planPriceData,
            this.locale,
            getPlanDurationInMonths(
                this.data.planPrice.plan?.durationInMonths || 0,
                this.data.planPrice.plan?.code,
                this.data.planPrice?.country?.code,
            ),
        )

        if (this.debugFlag) {
            this.debug
        }
    }

    setOverrideMinimalTotalCost(cost: number) {
        this.overrideTotalMinimalCost = cost
    }

    abstract get priceDescription(): string | ReactElement

    abstract get planDuration(): number

    abstract get cartRows(): ProductCartRows

    abstract get displayedPrice(): string

    get policyProvider() {
        return this.data.policyProvider
    }

    get claims(): SubscriptionClaimDTO[] {
        return this.data.claims || []
    }

    get claimCurrent(): SubscriptionClaimDTO | undefined {
        const activeClaims = this.data.claims?.filter(
            (claim) =>
                ![ClaimStatus.COMPLETED, ClaimStatus.DECLINED, ClaimStatus.HANDLEDBYUPGRADE].includes(
                    claim.claimStatus,
                ) && claim.claimType !== ClaimType.AUTOMATED_CANCEL,
        )

        return activeClaims?.[0]
    }

    get claimStatusTagProps(): TagProps | undefined {
        if (!this.data.claims?.[0]?.claimStatus) {
            return undefined
        }

        const translationOptions = {
            lng: this.locale,
        }

        const claimStatusMap = {
            // CANCEL CLAIMS
            [ClaimStatus.PENDING + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.sent', translationOptions),
            },

            // Automatic cancel claims
            [ClaimStatus.PENDING + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.HANDLEDBYUPGRADE + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.sent', translationOptions),
            },

            // SWAP CLAIMS
            [ClaimStatus.PENDING + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.PAYMENTERROR + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.PAYMENTRECEIVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.paymentReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.sent', translationOptions),
            },
        }

        const props = claimStatusMap[this.data.claims[0].claimStatus + this.data.claims[0].claimType]

        if (!props) {
            return undefined
        }

        return {
            className: 'tag--black',
            ...props,
        }
    }

    get uuid() {
        return this.data.uuid
    }

    get imei() {
        return this.data.productImei
    }

    get nickname() {
        if (this.data.nickname) {
            return this.data.nickname
        } else if (this.customer) {
            return translate('portal.products.nickname', {
                lng: this.locale,
                firstName: this.customer.firstName,
            })
        }

        return this.name
    }

    set nickname(value: string) {
        this.data.nickname = value
    }

    get hasNickname(): boolean {
        return !!this.data.nickname
    }

    get minimalTotalCost() {
        switch (this.data.planPrice?.country?.code) {
            case 'DK':
                return this.displayedPriceNumber * Math.min(this.plan.duration, 6)

            case 'NO':
                return this.displayedPriceNumber * Math.min(this.plan.duration, 12)

            default:
                return this.displayedPriceNumber * this.plan.duration
        }
    }

    get displayedPriceNumber() {
        return 0
    }

    get serviceDiscounts() {
        const discounts: SubscriptionProductDiscounts = {
            general: 0,
            ignoreVat: 0,
        }

        this.services.forEach((service) => {
            if (service.discountAffectsVat) {
                discounts.general += service.discountNumber
            } else {
                discounts.ignoreVat += service.discountNumber
            }
        })

        return discounts
    }

    get defaultDiscount() {
        if (!this.data.discount) {
            return 0
        }

        const discounts: SubscriptionProductDiscounts = this.serviceDiscounts

        return this.data.discount - (discounts.general + discounts.ignoreVat)
    }

    protected get discounts() {
        const discounts: SubscriptionProductDiscounts = this.serviceDiscounts

        if (this.data.discount) {
            discounts.general += this.data.discount - (discounts.general + discounts.ignoreVat)
        }

        return discounts
    }

    get baseSubscriptionCostNet() {
        if (!this.plan.monthlyPriceNumberNet) {
            throw new ErrorInSubscriptionDataSimple('C2', 'Base subscription cost 0 encountered')
        }

        return this.plan.monthlyPriceNumberNet
    }

    /**
     * @throws {Error} 'Base subscription cost 0 encountered'
     */
    get baseSubscriptionCostGross() {
        if (!this.plan.monthlyPriceNumberGross) {
            throw new ErrorInSubscriptionDataSimple('C2-1', 'Base subscription cost 0 encountered')
        }

        return this.plan.monthlyPriceNumberGross
    }

    get name() {
        return this.data.product?.translation?.name || this.data.product?.sku || ''
    }

    get description() {
        return this.data.product?.description || ''
    }

    get sku() {
        return this.data.product?.sku
    }

    get debug() {
        return 0
    }

    imageProps(width = 40): ImageProps | undefined {
        const url = isValidUrl(this.data.product?.imageUrl)

        if (url) {
            return {
                role: 'presentation',
                srcset: `${url.toString()} ${width}w, ${url.toString()} ${width * 2}w`,
                width,
            }
        }
    }
}
